import { values, mapValues, mergeWith } from 'lodash';
import {
  getTokenDecimals,
  formatTokenWei,
} from '@blockcatio/tabby-library/lib/utilities/tokenUtilities';

import { CampaignStatuses, RewardStatuses } from '../constants';
import getRewardStatus from '../utilities/getRewardStatus';
import getCampaignStatus from '../utilities/getCampaignStatus';
import { getTokenListByName, getTokenSymbolForAddress, getTokenLabelForAddress } from './token';

const campaignStatusKeys = values(CampaignStatuses);
const rewardStatusKeys = values(RewardStatuses);

/**
 * Returns a merge resolver for two objects, where each
 * has a status field, and the passed in statuses array
 * is a sorted one, where the latest one between the two
 * objects "wins" and is used.
 *
 * @param {String[]} statuses - sorted list of statuses
 * @param {Function} createStatus - f that converts status string to isntance
 * @return {Function} resolver
 */
const useLatestStatusStrategy = (statuses, createStatus) => (fetched, local) => {
  if (!local) return fetched;
  if (!fetched) return local;

  return {
    ...fetched,
    ...local,
    status: createStatus(statuses[
      Math.max(
        statuses.indexOf(local.status.toString()),
        statuses.indexOf(fetched.status.toString()),
      )
    ]),
  };
};

export const getCampaigns = ({
  campaigns: {
    fetchedCampaigns, localCampaigns, fetchedRewards, localRewards,
  },
}) => {
  const mergedCampaigns = mergeWith(
    {},
    fetchedCampaigns,
    localCampaigns,
    useLatestStatusStrategy(campaignStatusKeys, getCampaignStatus),
  );
  const mergedRewards = mergeWith(
    {},
    fetchedRewards,
    localRewards,
    useLatestStatusStrategy(rewardStatusKeys, getRewardStatus),
  );
  return values(mapValues(mergedCampaigns, campaign => ({
    ...campaign,
    rewards: campaign.rewards ? campaign.rewards.map(reward => mergedRewards[reward]) : [],
  }))).sort((a, b) => b.timestamp - a.timestamp);
};

export const getCampaign = (state, transactionHash) => {
  const campaigns = getCampaigns(state);
  const fullCampaign = campaigns.find(campaign => campaign.transactionHash === transactionHash);
  if (fullCampaign) {
    const tokenData = {
      tokenSymbol: 'N/A',
      tokenLabel: 'N/A',
      formattedTokensPerCode: 'N/A',
    };
    const tokenList = getTokenListByName(state);
    const { tokensPerCode, tokenAddress } = fullCampaign;
    try {
      const decimals = getTokenDecimals(tokenList, tokenAddress);
      tokenData.tokenSymbol = getTokenSymbolForAddress(state, tokenAddress);
      tokenData.tokenLabel = getTokenLabelForAddress(state, tokenAddress);
      tokenData.formattedTokensPerCode = formatTokenWei(tokensPerCode, decimals);
    } catch (error) {
      // not worrying about token not found for now.
    }
    return {
      ...fullCampaign,
      ...tokenData,
    };
  }
  return null;
};

export const isFetchingCampaigns = state => state.campaigns.isFetchingCampaigns;



// WEBPACK FOOTER //
// ./src/selectors/campaigns.js